import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "./AuthContext";
import { Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * New Engagement modal (admin/operator only).
 *
 * Core + Optional metadata. On submit → POST /api/console/engagements →
 * navigate to /console/engagements/{id} so the admin can immediately
 * start seeding sources, signals, findings, etc. Decision questions and
 * advisory-board drafts are authored inside the workspace, not here.
 *
 */
const EMPTY_FORM = {
  client_label: "", brand: "", ta: "", indication: "", scope: "",
  asset: "", mechanism: "", lifecycle_phase: "", engagement_objective: "",
  lead_methodologist: "", engagement_start: "", status: "Active",
  demo_category: "Pharma", demo_category_detail: "", kind: "live",
};

// `engagement` (optional) switches the modal into edit mode: fields are
// prefilled and submit PATCHes instead of POSTing.
export default function NewEngagementModal({ open, onClose, onCreated, engagement = null, onUpdated }) {
  const nav = useNavigate();
  const isEdit = !!engagement;

  // ── Form state ──────────────────────────────────────────────────
  const [form, setForm] = useState(() =>
    engagement
      ? Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, engagement[k] ?? EMPTY_FORM[k]]))
      : EMPTY_FORM,
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.client_label.trim()) return "Client label is required.";
    if (!form.brand.trim()) return "Brand / asset name is required.";
    if (!form.ta.trim()) return "Therapy area is required.";
    if (!form.indication.trim()) return "Indication is required.";
    if (!form.scope.trim() || form.scope.trim().length < 10)
      return "Scope must be at least 10 characters.";
    return "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const v = validate();
    if (v) {
      setErr(v);
      return;
    }
    setBusy(true);
    try {
      if (isEdit) {
        const { data } = await axios.patch(`${API}/console/engagements/${engagement.id}`, form);
        toast.success("Engagement updated");
        onUpdated?.(data);
        onClose?.();
        return;
      }
      const { data } = await axios.post(`${API}/console/engagements`, form);
      toast.success("Engagement created");
      onCreated?.();
      onClose?.();
      nav(`/console/engagements/${data.id}`);
    } catch (ex) {
      const detail = ex?.response?.data?.detail || ex.message || (isEdit ? "Update failed" : "Create failed");
      setErr(detail);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0F1825]/60 flex items-start justify-center px-4 py-8 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-engagement-title"
      data-testid="new-engagement-modal"
    >
      <div className="bg-white border border-[#E8E1D2] w-full max-w-3xl shadow-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-7 pt-6 pb-4 border-b border-[#E8E1D2]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21] mb-1">
              {isEdit ? "Edit engagement" : "New engagement · full setup"}
            </div>
            <h2
              id="new-engagement-title"
              className="font-serif text-2xl text-[#0F1825] leading-tight"
            >
              {isEdit ? `Edit ${engagement.brand || "engagement"}.` : "Spin up a fresh V2E engagement."}
            </h2>
            <p className="mt-1.5 text-[12.5px] text-[#5C6470] leading-relaxed max-w-lg">
              {isEdit
                ? "Update the brief metadata — asset, indication, scope, objective. Signals, findings and recommendations are not affected."
                : "Captures the core brief and optional context. Decision questions and advisory-board drafts are authored inside the workspace — every staff login can see the engagement as soon as it is created."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#A09680] hover:text-[#0F1825] transition-colors shrink-0"
            data-testid="new-engagement-close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-8">
          {/* SECTION · CORE */}
          <Section title="Core" subtitle="Required — the asset, indication and scope that frame every downstream signal.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Client label" required testid="fld-client_label">
                <Input value={form.client_label} onChange={(v) => set("client_label", v)} placeholder="e.g. Northbridge Therapeutics" />
              </Field>
              <Field label="Brand / asset name" required testid="fld-brand">
                <Input value={form.brand} onChange={(v) => set("brand", v)} placeholder="e.g. Halgrexa™" />
              </Field>
              <Field label="Therapy area" required testid="fld-ta">
                <Input value={form.ta} onChange={(v) => set("ta", v)} placeholder="e.g. Oncology — Genitourinary" />
              </Field>
              <Field label="Indication" required testid="fld-indication">
                <Input value={form.indication} onChange={(v) => set("indication", v)} placeholder="e.g. Advanced prostate cancer (mCSPC + mCRPC)" />
              </Field>
              <Field label="Scope" required testid="fld-scope" span={2}>
                <Textarea value={form.scope} onChange={(v) => set("scope", v)} rows={3} placeholder="A one-paragraph description of what this engagement covers, which functions it supports, and which geographies." />
              </Field>
            </div>
          </Section>

          {/* SECTION · OPTIONAL METADATA */}
          <Section title="Context" subtitle="Optional — everything here can be edited later inside the workspace.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Molecule / asset" testid="fld-asset">
                <Input value={form.asset} onChange={(v) => set("asset", v)} placeholder="e.g. relgenstat" />
              </Field>
              <Field label="Mechanism" testid="fld-mechanism">
                <Input value={form.mechanism} onChange={(v) => set("mechanism", v)} placeholder="e.g. Oral GnRH antagonist" />
              </Field>
              <Field label="Lifecycle phase" testid="fld-lifecycle_phase">
                <Input value={form.lifecycle_phase} onChange={(v) => set("lifecycle_phase", v)} placeholder="e.g. Marketed · indication expansion" />
              </Field>
              <Field label="Lead methodologist" testid="fld-lead_methodologist">
                <Input value={form.lead_methodologist} onChange={(v) => set("lead_methodologist", v)} placeholder="e.g. Dr. Aparna Rao · Senior HEOR Methodologist" />
              </Field>
              <Field label="Engagement start" testid="fld-engagement_start">
                <Input type="date" value={form.engagement_start} onChange={(v) => set("engagement_start", v)} />
              </Field>
              <Field label="Status" testid="fld-status">
                <Select
                  value={form.status}
                  onChange={(v) => set("status", v)}
                  options={["Active", "Paused", "Completed"]}
                />
              </Field>
              <Field label="Kind" testid="fld-kind">
                <Select
                  value={form.kind}
                  onChange={(v) => set("kind", v)}
                  options={[
                    { value: "live", label: "Live client engagement" },
                    { value: "demo", label: "Demo / illustrative" },
                  ]}
                />
              </Field>
              <Field label="Category detail" testid="fld-demo_category_detail">
                <Input value={form.demo_category_detail} onChange={(v) => set("demo_category_detail", v)} placeholder="e.g. Launch · adoption" />
              </Field>
              <Field label="Engagement objective" testid="fld-engagement_objective" span={2}>
                <Textarea value={form.engagement_objective} onChange={(v) => set("engagement_objective", v)} rows={2} placeholder="What this engagement should ultimately deliver — one or two sentences." />
              </Field>
            </div>
          </Section>

          {/* ERROR + ACTIONS */}
          {err && (
            <div
              className="border border-[#C84C21]/40 bg-[#C84C21]/5 text-[#C84C21] text-[12.5px] px-4 py-2.5"
              data-testid="new-engagement-error"
            >
              {err}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-4 border-t border-[#E8E1D2]">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="border border-[#E8E1D2] text-[#5C6470] hover:text-[#0F1825] hover:border-[#0F1825] text-[11.5px] font-mono uppercase tracking-[0.2em] px-4 py-2 transition-colors disabled:opacity-60"
              data-testid="new-engagement-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="bg-[#C84C21] hover:bg-[#a63e1a] text-white text-[11.5px] font-mono uppercase tracking-[0.2em] px-5 py-2 transition-colors disabled:opacity-60 inline-flex items-center gap-2 justify-center"
              data-testid="new-engagement-submit"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {busy ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create engagement")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Small building blocks ────────────────────────────────────── */

function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-3">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C84C21]">
          {title}
        </div>
        {subtitle && (
          <p className="mt-1 text-[12px] text-[#5C6470] leading-relaxed max-w-2xl">
            {subtitle}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, children, span = 1, testid }) {
  const spanCls = span === 2 ? "md:col-span-2" : "";
  return (
    <label className={`flex flex-col gap-1.5 ${spanCls}`} data-testid={`${testid}-wrap`}>
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6470]">
        {label}
        {required && <span className="text-[#C84C21] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="border border-[#E8E1D2] bg-[#FDFAF3] px-3 py-2 text-[13px] text-[#0F1825] placeholder:text-[#A09680] focus:outline-none focus:border-[#0F1825] transition-colors"
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="border border-[#E8E1D2] bg-[#FDFAF3] px-3 py-2 text-[13px] text-[#0F1825] placeholder:text-[#A09680] focus:outline-none focus:border-[#0F1825] transition-colors resize-y leading-relaxed"
    />
  );
}

function Select({ value, onChange, options }) {
  const norm = (o) => (typeof o === "string" ? { value: o, label: o } : o);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-[#E8E1D2] bg-[#FDFAF3] px-3 py-2 text-[13px] text-[#0F1825] focus:outline-none focus:border-[#0F1825] transition-colors"
    >
      {options.map((o) => {
        const n = norm(o);
        return (
          <option key={n.value} value={n.value}>
            {n.label}
          </option>
        );
      })}
    </select>
  );
}
